// src/services/aeo/publishRefusal.ts
//
// Publish refusals (H20, H20a; scope 2.23). Once Delivery's publish gate is
// on main, both publish routes (a fix draft in Site issues, a page's
// changes in the Pages workshop) refuse a change the client has not said
// yes to: 409 with the slug in errors[0] and, from H20a, the key and the
// doc that still needs their yes. This module reads that refusal, records a
// staff override with a name, a reason and where the client said yes, and
// serves one sample refusal on the dev server so the screen can be seen.
//
// The rule stays as built (YW's default, 2 Oct 2026): page copy needs the
// client's yes; structured data and internal links a named staff yes. An
// override is set only by a named person, holds for every later change on its
// key until someone removes it, and shows in the doc's approval record.
//
// The override goes through the task-approvals route the packs already use
// (clientReview.setTaskApproval), with the name, reason and where the client
// said yes (H20a). Recording it is a Phase 2 write: the screen offers it only
// with the Docs write switch on, and the sample refusal answers only where the
// Docs write sample does.
//
//   readPublishRefusal(err)                 the refusal in a failed publish, or null
//   samplePagePublish(businessId)           the sample's refused publish (dev server only), or undefined
//   recordClientYes(businessId, key, body)  PUT /businesses/{id}/task-approvals/{key}  (H20a)

import axios from "axios";
import type { ApprovalOverrideIn, ApprovalOverrideRecord, PublishRefusal, PublishRefusalCode } from "./deliveryTypes";
import type { AeoPagePublishResult } from "./types";
import { isClientReviewLive, listTaskApprovals, setTaskApproval, type ApprovalOverride } from "./clientReview";
import { DOCS_WRITE_SAMPLE_SERVES } from "../../oura/docs/writeFlag";

/** The live routes: the publish calls already sit behind this switch (services/aeo/index.ts). */
const LIVE = import.meta.env.VITE_ENABLE_OURA_LIVE_API === "true";

/**
 * The sample refusal answers only where the Docs write sample does: the dev
 * server, with Oura and the docs on fixtures. Never a build.
 */
export const PUBLISH_SAMPLE_SERVES = DOCS_WRITE_SAMPLE_SERVES && !LIVE;

export const PUBLISH_REFUSAL_CODES: readonly PublishRefusalCode[] = ["APPROVAL_REQUIRED", "draft_not_on_the_plan", "wrong_item_for_draft"];

/** What a refusal says when the server sends no sentence. */
export const PUBLISH_REFUSAL_MESSAGE: Record<PublishRefusalCode, string> = {
    APPROVAL_REQUIRED: "The client has not said yes to this yet.",
    draft_not_on_the_plan: "This draft is not on the plan, so it can't go live from here.",
    wrong_item_for_draft: "This draft belongs to another item on the plan.",
};

const isRefusalCode = (v: unknown): v is PublishRefusalCode => typeof v === "string" && (PUBLISH_REFUSAL_CODES as readonly string[]).includes(v);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);

/**
 * The refusal in a failed publish: a 409 whose errors[0] is one of H20's
 * slugs. Reads H20a's fields in either case style; a refusal from a server
 * before H20a still names itself, with no key and no doc.
 */
export function readPublishRefusal(err: unknown): PublishRefusal | null {
    if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
    const body = (err.response.data ?? {}) as { errors?: unknown[]; message?: unknown; data?: Record<string, unknown> | null };
    const code = Array.isArray(body.errors) ? body.errors[0] : undefined;
    if (!isRefusalCode(code)) return null;
    const data = body.data ?? {};
    const pick = (camel: string, snake: string): unknown => data[camel] ?? data[snake];
    const approvalKey = str(pick("approvalKey", "approval_key"));
    const canOverride = pick("canOverride", "can_override");
    return {
        code,
        message: str(body.message) ?? PUBLISH_REFUSAL_MESSAGE[code],
        approvalKey,
        docId: str(pick("docId", "doc_id")),
        docTitle: str(pick("docTitle", "doc_title")),
        // Only a client's yes can be waived, only on a key, and only when the server
        // says so. Absent reads as not allowed: a gate on the client's approval fails
        // closed, so an older or partial refusal offers "Open the doc", never the override.
        canOverride: code === "APPROVAL_REQUIRED" && approvalKey !== null && canOverride === true,
    };
}

/* ------------------------------------------------------------------------- *
 * The sample: refused publishes in the Pages workshop on nyonya-table
 * ------------------------------------------------------------------------- */

/** The sample business: its Pages workshop has no live site, so it gets this publish on the dev server only. */
const SAMPLE_BUSINESS = "nyonya-table";
/** Page copy needs the client's yes (H20): the key the sample refuses on. Delivery's G1 reads the same key, so both screens agree. */
const SAMPLE_KEY = "B1:page_copy";

/** The sample's page copy key, where the workshop records the client's yes: the sample business on the dev server only, else null, so a live item reads only its own key. */
export function samplePageCopyKey(businessId: string): string | null {
    return PUBLISH_SAMPLE_SERVES && businessId === SAMPLE_BUSINESS ? SAMPLE_KEY : null;
}

/** The Delivery item the sample's signature page belongs to, named the same on both screens. */
export const SAMPLE_PAGE_ITEM = "G1 · Your signature page";

/** Whether the client's yes on page copy is recorded for the sample (the one approval source both screens read). */
export async function sampleClientYes(businessId: string): Promise<boolean> {
    if (!PUBLISH_SAMPLE_SERVES || businessId !== SAMPLE_BUSINESS) return false;
    const overrides = await listTaskApprovals(SAMPLE_BUSINESS).catch(() => [] as ApprovalOverride[]);
    return overrides.some((o) => isRecordedYes(o, SAMPLE_KEY));
}

/** The sample's site is on Wix: Oura cannot write to it, so its page copy is pasted in, never published from here. */
export function samplePasteOnly(businessId: string): string | undefined {
    return PUBLISH_SAMPLE_SERVES && businessId === SAMPLE_BUSINESS ? "Wix" : undefined;
}
/** The sample's draft on a page the plan covers, and on one it does not. */
export const SAMPLE_DRAFT_ON_PLAN = "sample-page-draft";
export const SAMPLE_DRAFT_OFF_PLAN = "sample-page-draft-off-plan";
/** The pages the sample's plan covers (Delivery's sample pages). Any other page's draft is not on the plan. */
const SAMPLE_PLAN_PAGES = new Set(["/", "/menu", "/tasting-menu", "/catering", "/private-dining", "/prices", "/contact", "/gift-vouchers"]);

/** Whether the sample's plan covers this page. */
export function sampleOnPlan(url: string): boolean {
    let path = url;
    try {
        path = new URL(url, "https://sample.invalid").pathname;
    } catch {
        /* a bare path already */
    }
    return SAMPLE_PLAN_PAGES.has(path.replace(/\/+$/, "") || "/");
}

/** A staff override that waives the client's yes on this key, with its reason (H20a). */
function isRecordedYes(o: ApprovalOverride, key: string): boolean {
    return o.taskId === key && o.source === "staff" && o.requirement === "not_required" && Boolean(o.whereClientSaidYes);
}

/**
 * The Pages workshop's publish on the sample business, on the dev server
 * only (undefined anywhere else, so the screen keeps hiding Publish there).
 * A page off the plan is refused "not on the plan", with nothing to
 * override. A page on it is refused for the client's yes in the Answer Plan
 * until someone records it; then a plain word that a sample publishes nothing.
 */
export function samplePagePublish(businessId: string): ((args: { draftId: string; dismissed: string[] }) => Promise<AeoPagePublishResult>) | undefined {
    if (!PUBLISH_SAMPLE_SERVES || businessId !== SAMPLE_BUSINESS) return undefined;
    return async ({ draftId }) => {
        await new Promise((r) => window.setTimeout(r, 600));
        if (draftId === SAMPLE_DRAFT_OFF_PLAN) {
            const refusal: PublishRefusal = {
                code: "draft_not_on_the_plan",
                message: "This page is not on the plan, so its draft can't go live from here. Add the page to the plan in Delivery first.",
                approvalKey: null,
                docId: null,
                docTitle: null,
                canOverride: false,
            };
            return { ok: false, kind: "error", message: refusal.message, refusal };
        }
        const overrides = await listTaskApprovals(SAMPLE_BUSINESS).catch(() => [] as ApprovalOverride[]);
        if (overrides.some((o) => isRecordedYes(o, SAMPLE_KEY))) {
            return { ok: false, kind: "error", recorded: true, message: "The client's yes is recorded. This is a sample business with no live site, so nothing was published." };
        }
        const refusal: PublishRefusal = {
            code: "APPROVAL_REQUIRED",
            message: `The client has not said yes to this page's new wording yet (${SAMPLE_PAGE_ITEM} in Delivery). It needs their yes in the Answer Plan.`,
            approvalKey: SAMPLE_KEY,
            docId: "doc_f1_answer_plan",
            docTitle: "Answer Plan (S1, S3, B1, G4)",
            canOverride: true,
        };
        return { ok: false, kind: "error", message: refusal.message, refusal };
    };
}

/* ------------------------------------------------------------------------- *
 * The override (H20a)
 * ------------------------------------------------------------------------- */

export type OverrideResult = { ok: true; data: ApprovalOverrideRecord } | { ok: false; message: string };

/**
 * Record the client's yes for one key, as a named person, through the
 * task-approvals route the packs already use (clientReview.setTaskApproval):
 * every later change on that key no longer waits on the doc, until someone
 * removes it, and the record says who, why and where they said yes. The doc
 * drawer's approval record lists it. Publishing stays the person's own click.
 */
export async function recordClientYes(businessId: string, key: string, body: ApprovalOverrideIn): Promise<OverrideResult> {
    const name = body.name.trim();
    const reason = body.reason.trim();
    const where = body.whereClientSaidYes.trim();
    if (!name) return { ok: false, message: "Your name is missing. Sign in again." };
    if (!where) return { ok: false, message: "Say where the client said yes." };
    if (!reason) return { ok: false, message: "Give the reason." };
    // Live, the task-approvals route must be live too; otherwise it would land in the packs' sample store.
    if (!PUBLISH_SAMPLE_SERVES && !(LIVE && isClientReviewLive)) return { ok: false, message: "Recording the client's yes is not available here yet." };
    try {
        const o = await setTaskApproval(businessId, key, body.requirement, body.showToClient ?? true, { name, reason, whereClientSaidYes: where });
        return {
            ok: true,
            data: {
                key,
                requirement: o?.requirement ?? body.requirement,
                source: "staff",
                setBy: o?.setBy ?? name,
                setAt: o?.setAt ?? new Date().toISOString(),
                reason: o?.reason ?? reason,
                whereClientSaidYes: o?.whereClientSaidYes ?? where,
            },
        };
    } catch (err) {
        const message = axios.isAxiosError(err) ? str(err.response?.data?.message) : null;
        return { ok: false, message: message ?? "That did not save. Try again." };
    }
}
